import { AppError, getRequestIdFromError, parseApiErrorBody } from "@/lib/observability/app-error"

import { ErrorCode, NON_REPORTABLE_CODES, UserMessage, type ErrorCodeValue } from "./codes"

export interface NormalizedError {
  userMessage: string
  code?: ErrorCodeValue | string
  requestId?: string
  reportable: boolean
  original: unknown
}

const INTERNAL_MESSAGE_PATTERNS = [
  /pgrst/i,
  /postgres/i,
  /relation .* does not exist/i,
  /column .* does not exist/i,
  /syntax error/i,
  /invalid input syntax/i,
  /row-level security/i,
  /permission denied for/i,
  /internal server error/i,
  /unexpected token/i,
  /cannot read propert/i,
  /typeerror:/i,
  /referenceerror:/i,
  /fetch failed/i,
  /networkerror/i,
  /econnrefused/i,
  /sql/i,
  /rpc failed/i,
  /jwt expired/i,
  /invalid jwt/i,
  /service role key/i,
  /supabase_url/i,
  /openai status/i,
  /sync-worker invoke failed/i,
]

function readErrorCode(error: unknown): string | undefined {
  if (error instanceof AppError) return error.code
  if (error && typeof error === "object" && "code" in error) {
    const value = (error as { code?: unknown }).code
    return typeof value === "string" ? value : undefined
  }
  return undefined
}

function looksLikeInternalMessage(message: string): boolean {
  return INTERNAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))
}

function mapAuthMessage(message: string): string {
  const lower = message.toLowerCase()
  if (lower.includes("invalid login credentials") || lower.includes("invalid email or password")) {
    return UserMessage.AUTH_INVALID
  }
  if (lower.includes("email not confirmed")) {
    return "Confirm your email address before signing in."
  }
  if (lower.includes("user already registered")) {
    return "An account with this email already exists."
  }
  return UserMessage.AUTH_GENERIC
}

function mapPostgrestMessage(message: string, code?: string): string | null {
  if (code === "PGRST301" || /jwt expired|invalid jwt|not authenticated/i.test(message)) {
    return UserMessage.UNAUTHORIZED
  }
  if (/permission denied|row-level security|42501/i.test(message)) {
    return UserMessage.FORBIDDEN
  }
  if (/not found|no rows|pgrst116/i.test(message)) {
    return UserMessage.NOT_FOUND
  }
  if (looksLikeInternalMessage(message)) return UserMessage.GENERIC
  return null
}

function isBenignAuthMessage(message: string): boolean {
  return /invalid login|email not confirmed|already registered|sign-in failed/i.test(message)
}

export function normalizeError(error: unknown): NormalizedError {
  const requestId = getRequestIdFromError(error)
  const code = readErrorCode(error)

  if (error instanceof AppError) {
    const userMessage = error.userMessage ?? error.message
    const resolvedCode = error.code ?? code
    return {
      userMessage,
      code: resolvedCode,
      requestId: error.requestId ?? requestId,
      reportable: error.reportable ?? !NON_REPORTABLE_CODES.has(resolvedCode ?? ""),
      original: error,
    }
  }

  if (error instanceof Error) {
    const postgrestCode =
      "code" in error && typeof (error as { code?: unknown }).code === "string"
        ? String((error as { code?: string }).code)
        : undefined

    const mappedPostgrest = mapPostgrestMessage(error.message, postgrestCode)
    if (mappedPostgrest) {
      const mappedCode =
        mappedPostgrest === UserMessage.UNAUTHORIZED
          ? ErrorCode.UNAUTHORIZED
          : mappedPostgrest === UserMessage.FORBIDDEN
            ? ErrorCode.FORBIDDEN
            : mappedPostgrest === UserMessage.NOT_FOUND
              ? ErrorCode.NOT_FOUND
              : ErrorCode.INTERNAL
      return {
        userMessage: mappedPostgrest,
        code: mappedCode,
        requestId,
        reportable: !NON_REPORTABLE_CODES.has(mappedCode),
        original: error,
      }
    }

    if (code === ErrorCode.INTERNAL || looksLikeInternalMessage(error.message)) {
      return {
        userMessage: UserMessage.GENERIC,
        code: ErrorCode.INTERNAL,
        requestId,
        reportable: true,
        original: error,
      }
    }

    if (isBenignAuthMessage(error.message)) {
      return {
        userMessage: mapAuthMessage(error.message),
        code: ErrorCode.UNAUTHORIZED,
        requestId,
        reportable: false,
        original: error,
      }
    }

    if (NON_REPORTABLE_CODES.has(code ?? "")) {
      return {
        userMessage: error.message,
        code,
        requestId,
        reportable: false,
        original: error,
      }
    }

    return {
      userMessage: error.message,
      code,
      requestId,
      reportable: true,
      original: error,
    }
  }

  if (typeof error === "string" && looksLikeInternalMessage(error)) {
    return {
      userMessage: UserMessage.GENERIC,
      code: ErrorCode.INTERNAL,
      requestId,
      reportable: true,
      original: error,
    }
  }

  return {
    userMessage: typeof error === "string" ? error : UserMessage.GENERIC,
    code: ErrorCode.INTERNAL,
    requestId,
    reportable: true,
    original: error,
  }
}

/** The scenario-write envelopes (`conflict`, `validation`) the app maps here
 *  never reach this site -- the only edge-function call it makes is the
 *  waitlist submission -- so they fall through to the generic path. */
export function normalizeApiErrorBody(
  data: Record<string, unknown> | null,
  fallbackMessage = UserMessage.GENERIC,
): NormalizedError {
  const parsed = parseApiErrorBody(data)
  const code = parsed.code ?? (typeof data?.error === "string" ? data.error : undefined)

  if (code === ErrorCode.INTERNAL || looksLikeInternalMessage(parsed.message)) {
    return {
      userMessage: UserMessage.GENERIC,
      code: ErrorCode.INTERNAL,
      requestId: parsed.requestId,
      reportable: true,
      original: data,
    }
  }

  return {
    userMessage: parsed.message || fallbackMessage,
    code,
    requestId: parsed.requestId,
    reportable: !NON_REPORTABLE_CODES.has(code ?? ""),
    original: data,
  }
}

export function toAppError(error: unknown): AppError {
  const normalized = normalizeError(error)
  if (error instanceof AppError) return error
  return new AppError(normalized.userMessage, {
    requestId: normalized.requestId,
    code: normalized.code,
    reportable: normalized.reportable,
    cause: error,
  })
}
